/**
 * Compile the discovery queries Radar should auto-pull.
 *
 * Listening rules today are stored but only matched against RSS already in
 * Radar. Keywords on Coverage are filters, not ingest. This compiler is the
 * missing join: listen-target handles + owned @mentions + tracked keywords
 * become the query set a discovery beat (SERP / public-feed poll) should run.
 *
 * Query dialect is the same as listening-query.ts. `from:@handle` is an ingest
 * hint meaning "posts authored by this account", not a mention of them.
 *
 * Sync identity: relevance toggles change `query_include`, so matching by exact
 * query creates duplicate rules. We stamp ` · watch:<key>` on auto-created
 * names and match that first.
 */
import { formatChipQuery } from './listening-query';
import type { ApiListeningRule, ApiSourceAccount, ApiTrackedKeyword } from '@/client/types/marketing/api';

export type WatcherKind = 'author_feed' | 'mention' | 'keyword' | 'rule';
export type WatchRelevance = 'relevant' | 'all_from_targets';

export interface CompiledWatcher {
  /** Stable across reloads so Sync can skip already-created rules. */
  key: string;
  kind: WatcherKind;
  name: string;
  queryInclude: string;
  queryExclude: string | null;
  channels: string[];
  origin: string;
  matchingRuleId: number | null;
}

/** Machine suffix Sync writes / matches. Stable across relevance toggles. */
export const WATCH_KEY_SUFFIX = (key: string) => ` · watch:${key}`;

export function ruleNameForWatcher(displayName: string, key: string): string {
  const base = displayName.replace(/\s·\swatch:[^\s]+$/, '').trim();
  return `${base}${WATCH_KEY_SUFFIX(key)}`;
}

export function parseWatchKeyFromRuleName(name: string): string | null {
  const m = name.match(/\s·\swatch:([^\s]+)\s*$/);
  return m?.[1] ?? null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

function normQuery(q: string): string {
  return q.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** @handle when the registry value looks like a handle; quoted name otherwise. */
export function mentionTerm(handle: string): string {
  const t = handle.trim();
  if (!t) return '';
  if (t.includes(' ')) {
    const cleaned = t.replace(/^@/, '');
    return `"${cleaned}"`;
  }
  return t.startsWith('@') ? t : `@${t}`;
}

export function fromTerm(handle: string): string {
  const m = mentionTerm(handle);
  if (!m) return '';
  // `from:"Accra Expats"` tokenizes as two terms; quoted names stay quoted.
  if (m.startsWith('"')) return m;
  return `from:${m}`;
}

function keywordQuery(k: ApiTrackedKeyword): string {
  const terms = strings(k.terms).filter(Boolean);
  const mode = k.match_mode === 'all' ? 'all' : 'any';
  return formatChipQuery(terms, mode);
}

function brandRelevanceQuery(owned: ApiSourceAccount[], keywords: ApiTrackedKeyword[]): string {
  const mentions = owned
    .filter((a) => a.active && a.use_as_listen)
    .map((a) => mentionTerm(a.handle))
    .filter(Boolean);
  const kw = keywords
    .filter((k) => k.enabled && strings(k.channels).includes('social'))
    .map(keywordQuery)
    .filter(Boolean);
  const parts = [...mentions, ...kw];
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `(${parts.join(' OR ')})`;
}

function findRule(
  rules: ApiListeningRule[],
  watcherKey: string,
  queryInclude: string,
): ApiListeningRule | undefined {
  const byKey = rules.find((r) => parseWatchKeyFromRuleName(r.name) === watcherKey);
  if (byKey) return byKey;
  const n = normQuery(queryInclude);
  return rules.find((r) => normQuery(r.query_include) === n);
}

/**
 * Topics saved in Trends already have a rule (`listening_rule_id`, stamp
 * ` · watch:keyword:<tkid>`). Match that before falling back to query text so
 * Sync does not create a second watcher.
 */
export function matchWatchRule(
  rules: ApiListeningRule[],
  watcherKey: string,
  queryInclude: string,
  listeningRuleId?: number | null,
): ApiListeningRule | undefined {
  if (listeningRuleId != null) {
    const byId = rules.find((r) => r.lrid === listeningRuleId);
    if (byId) return byId;
  }
  return findRule(rules, watcherKey, queryInclude);
}

export function compileWatchers(opts: {
  accounts: ApiSourceAccount[];
  keywords: ApiTrackedKeyword[];
  rules: ApiListeningRule[];
  relevance: WatchRelevance;
}): CompiledWatcher[] {
  const { accounts, keywords, rules, relevance } = opts;
  const listen = accounts.filter((a) => a.active && a.use_as_listen && !a.owned);
  const owned = accounts.filter((a) => a.active && a.owned);
  const brand = brandRelevanceQuery(owned, keywords);
  const out: CompiledWatcher[] = [];

  for (const a of listen) {
    const from = fromTerm(a.handle);
    if (!from) continue;
    const key = `author:${a.said}`;
    const queryInclude =
      relevance === 'relevant' && brand ? `(${from}) AND ${brand}` : from;
    const match = matchWatchRule(rules, key, queryInclude);
    out.push({
      key,
      kind: 'author_feed',
      name: `Auto: posts by ${a.display_name || a.handle}`,
      queryInclude,
      queryExclude: null,
      channels: ['social'],
      origin: `${a.handle} · ${a.ownership || 'listen target'}`,
      matchingRuleId: match?.lrid ?? null,
    });
  }

  for (const a of owned) {
    const mention = mentionTerm(a.handle);
    if (!mention) continue;
    const key = `mention:${a.said}`;
    const match = matchWatchRule(rules, key, mention);
    out.push({
      key,
      kind: 'mention',
      name: `Auto: mentions of ${a.handle}`,
      queryInclude: mention,
      queryExclude: null,
      channels: ['social'],
      origin: `Owned ${a.platform} account`,
      matchingRuleId: match?.lrid ?? null,
    });
  }

  for (const k of keywords.filter((x) => x.enabled)) {
    const q = keywordQuery(k);
    if (!q) continue;
    const key = `keyword:${k.tkid}`;
    const channels = strings(k.channels);
    const match = matchWatchRule(rules, key, q, k.listening_rule_id);
    out.push({
      key,
      kind: 'keyword',
      name: `Auto: ${k.label}`,
      queryInclude: q,
      queryExclude: null,
      channels: channels.length ? channels : ['social'],
      origin: 'Tracked keyword',
      matchingRuleId: match?.lrid ?? null,
    });
  }

  const autoKeys = new Set(out.map((w) => w.key));
  const autoQueries = new Set(out.map((w) => normQuery(w.queryInclude)));
  for (const r of rules.filter((x) => x.enabled)) {
    const stamped = parseWatchKeyFromRuleName(r.name);
    if (stamped && autoKeys.has(stamped)) continue;
    if (autoQueries.has(normQuery(r.query_include))) continue;
    out.push({
      key: `rule:${r.lrid}`,
      kind: 'rule',
      name: r.name,
      queryInclude: r.query_include,
      queryExclude: r.query_exclude,
      channels: strings(r.channels),
      origin: 'Custom rule',
      matchingRuleId: r.lrid,
    });
  }

  return out;
}

export function unsyncedWatchers(watchers: CompiledWatcher[]): CompiledWatcher[] {
  return watchers.filter((w) => w.kind !== 'rule' && w.matchingRuleId == null);
}
